import React, { useEffect, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Field, FastField } from 'formik';
import axios from 'axios';
import { getHeaders } from '../../../request';
import { useMoment } from '../../../hooks';
import validationSchema from './validationSchema';
import { temPerfilRBAC } from '../../../secutity/rbac';
import { PERFIL } from '../../../common/Constants';
import { SttGrid, SttDatePicker, SttInput, SttAutocomplete, SttMaskedInput, SttTranslateHook } from '@stt-componentes/core';
import { SttClearButton, SttSearchButton } from "@stt-componentes/core-v2";
import { DATA_DE, DATA_ATE, NOME_SOLICITANTE, NOME_PACIENTE, CPF_PACIENTE, CNS_PACIENTE, UF, MUNICIPIO } from './fieldNames';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import usuario from '../../../signals/usuario';
import { useSignals } from '@preact/signals-react/runtime';

const useStyles = makeStyles((theme) => ({
	buttonWrapper: {
		display: 'flex',
		alignItems: 'center'
	}
}));

const estadoInicialForm = {
	de: null,
	ate: null,
	solicitante: '',
	paciente: '',
	cpf: '',
	cns: '',
	uf: null,
	municipio: null
};

const Form = (props) => {
	const { callbackBusca, buscaEmAndamento, page, count, orderBy, order, buscar, resetPageOrder } = props;

	useSignals();
	const { strings } = useContext(SttTranslateHook.I18nContext);
	const schema = validationSchema(strings);
	const moment = useMoment();
	const formRef = useRef();
	const classes = useStyles();

	const UTILITARIOS_API_BASE_URL = global.gConfig.url_base_utilitarios;

	const carregarUfs = useSignal(false);
	const ufs = useSignal([]);
	const municipios = useSignal([]);

	useEffect(() => {
		if (temPerfilRBAC(usuario, [PERFIL.ADMINISTRADOR, PERFIL.ADMINISTRADOR_ESTADUAL])) {
			carregarUfs.value = true;
		}
	}, []);

	useSignalEffect(() => {
		if (carregarUfs.value) {
			let url = `${UTILITARIOS_API_BASE_URL}/localizacao/estado`;
			if (temPerfilRBAC(usuario, PERFIL.ADMINISTRADOR_ESTADUAL)) {
				const perfil = usuario.value.perfisRBAC.filter((p) => p.identificador === PERFIL.ADMINISTRADOR_ESTADUAL);
				const estados = perfil[0].estados;
				const idsUf = estados.map((e) => e.id);
				url = `${url}?ids=${idsUf.join(',')}`;
			}
			axios
				.get(url)
				.then((response) => {
					if (response.data) {
						const { itens } = response.data.data;
						ufs.value = itens;
					}
				})
				.catch((err) => console.log(err));
		}
	});

	useSignalEffect(() => {
		if (buscar.value && formRef.current) {
			formRef.current.handleSubmit();
		}
	});

	const handleChangeUf = (uf) => {
		if (uf) {
			axios
				.get(`${UTILITARIOS_API_BASE_URL}/localizacao/cidade?id-estado=${uf.id}`)
				.then((response) => {
					if (response.data) {
						const { itens } = response.data.data;
						municipios.value = itens;
					} else {
						municipios.value = [];
					}
				})
				.catch((err) => console.log(err));
		} else {
			municipios.value = [];
		}
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={estadoInicialForm}
			validationSchema={schema}
			onSubmit={(data, { setSubmitting }) => {
				setSubmitting(true);
				buscaEmAndamento.value = true;

				let params = {
					start: page.value * count.value,
					count: count.value
				};
				if (data.de) {
					params.de = moment(data.de).format('YYYY-MM-DD');
				}
				if (data.ate) {
					params.ate = moment(data.ate).format('YYYY-MM-DD');
				}
				if (data.solicitante) {
					params.solicitante = data.solicitante.trim();
				}
				if (data.paciente) {
					params.paciente = data.paciente.trim();
				}
				if (data.cpf) {
					params.cpf = data.cpf;
				}
				if (data.cns) {
					params.cns = data.cns;
				}
				if (data.uf) {
					params.uf = data.uf.id;
				}
				if (data.municipio) {
					params.municipio = data.municipio.id;
				}
				if (orderBy.value && order.value) {
					params.sort = orderBy.value;
					params.direction = order.value;
				}

				axios
					.get(`${global.gConfig.url_base_espiro}/solicitacao`, { params, headers: getHeaders() })
					.then((response) => {
						const dados = response.data.data;
						callbackBusca(dados);
					})
					.catch((err) => {
						console.error(err);
						callbackBusca(null);
					})
					.finally(() => {
						setSubmitting(false);
						buscaEmAndamento.value = false;
					});
			}}
		>
			{({ isSubmitting, values, handleSubmit, resetForm, submitForm }) => {
				return (
					<form
						onSubmit={handleSubmit}
						noValidate
					>
						<SttGrid
							container
							spacing={3}
						>
							<SttGrid
								item
								xs={12}
								sm={12}
								md={6}
								lg={4}
							>
								<SttGrid
									container
									spacing={3}
								>
									<SttGrid
										item
										xs={12}
										sm={6}
									>
										<FastField name={DATA_DE}>
											{({ field: { name, value }, form: { setFieldValue, setFieldError, setFieldTouched }, meta }) => {
												return (
													<SttDatePicker
														label={strings.dataInicialSolicitacao}
														inputprops={{
															name: name
														}}
														disableFuture
														maxDate={new Date()}
														error={meta.touched && meta.error ? true : false}
														onError={(error) => {
															setFieldError(DATA_DE, meta.error || error);
														}}
														value={value}
														onBlur={() => {
															setFieldTouched(DATA_DE, true);
														}}
														helperText={meta.touched && meta.error ? meta.error : undefined}
														onChange={(date) => setFieldValue(DATA_DE, date, true)}
														onClose={() => setFieldTouched(DATA_DE, true)}
													/>
												);
											}}
										</FastField>
									</SttGrid>
									<SttGrid
										item
										xs={12}
										sm={6}
									>
										<FastField name={DATA_ATE}>
											{({ field: { name, value }, form: { setFieldValue, setFieldError, setFieldTouched }, meta }) => {
												return (
													<SttDatePicker
														label={strings.dataFinalSolicitacao}
														inputprops={{
															name: name
														}}
														disableFuture
														maxDate={new Date()}
														error={meta.touched && meta.error ? true : false}
														onError={(error) => {
															setFieldError(DATA_ATE, meta.error || error);
														}}
														value={value}
														onBlur={() => {
															setFieldTouched(DATA_ATE, true);
														}}
														helperText={meta.touched && meta.error ? meta.error : undefined}
														onChange={(date) => setFieldValue(DATA_ATE, date, true)}
														onClose={() => setFieldTouched(DATA_ATE, true)}
													/>
												);
											}}
										</FastField>
									</SttGrid>
								</SttGrid>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={NOME_SOLICITANTE}>
									{({ field }) => (
										<SttInput
											{...field}
											label={strings.solicitante}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={NOME_PACIENTE}>
									{({ field }) => (
										<SttInput
											{...field}
											label={strings.paciente}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={CPF_PACIENTE}>
									{({ field, meta }) => (
										<SttMaskedInput
											{...field}
											mask="cpf"
											label={strings.cpfPaciente}
											error={meta.touched && meta.error ? true : false}
											helperText={meta.touched && meta.error ? meta.error : undefined}
										/>
									)}
								</FastField>
							</SttGrid>
							<SttGrid
								item
								xs={12}
								sm={6}
								md={3}
								lg={3}
								xl={2}
							>
								<FastField name={CNS_PACIENTE}>
									{({ field, meta }) => (
										<SttMaskedInput
											{...field}
											mask="cns"
											label={strings.cnsPaciente}
											error={meta.touched && meta.error ? true : false}
											helperText={meta.touched && meta.error ? meta.error : undefined}
										/>
									)}
								</FastField>
							</SttGrid>
							{temPerfilRBAC(usuario, [PERFIL.ADMINISTRADOR, PERFIL.ADMINISTRADOR_ESTADUAL]) && (
								<>
									<SttGrid
										item
										xs={12}
										sm={3}
										md={2}
										lg={1}
									>
										<Field name={UF}>
											{({ field: { name, value, onBlur }, form: { setFieldValue } }) => (
												<SttAutocomplete
													inputprops={{
														name: name,
														label: strings.uf
													}}
													getOptionLabel={(option) => option?.sigla || ''}
													getOptionSelected={(option, val) => option?.id === val?.id}
													options={ufs.value}
													value={value}
													onBlur={onBlur}
													onChange={(e, item) => {
														const uf = item || null;
														setFieldValue(UF, uf);
														setFieldValue(MUNICIPIO, null);
														handleChangeUf(uf);
													}}
												/>
											)}
										</Field>
									</SttGrid>
									<SttGrid
										item
										xs={12}
										sm={9}
										md={4}
										lg={4}
										xl={3}
									>
										<Field name={MUNICIPIO}>
											{({ field: { name, value, onBlur }, form: { setFieldValue } }) => (
												<SttAutocomplete
													inputprops={{
														name: name,
														label: strings.municipio
													}}
													getOptionLabel={(option) => option?.nome || ''}
													getOptionSelected={(option, v) => option?.id === v?.id}
													options={municipios.value}
													value={value}
													onBlur={onBlur}
													onChange={(e, item) => {
														setFieldValue(MUNICIPIO, item || null);
													}}
												/>
											)}
										</Field>
									</SttGrid>
								</>
							)}
						</SttGrid>
						<SttGrid
							container
							spacing={3}
						>
							<SttGrid
								item
								xs={12}
								className={classes.buttonWrapper}
							>
								<SttSearchButton
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitting}
									nomarginleft="true"
									onClick={() => {
										resetPageOrder();
										submitForm();
									}}
								>
									{strings.pesquisar}
								</SttSearchButton>
								<SttClearButton
									type="button"
									variant="outlined"
									color="primary"
									disabled={isSubmitting}
									onClick={() => {
										resetPageOrder();
										resetForm({
											values: estadoInicialForm
										});
										callbackBusca(null);
									}}
								>
									{strings.limpar}
								</SttClearButton>
							</SttGrid>
						</SttGrid>
					</form>
				);
			}}
		</Formik>
	);
};

export default Form;
